import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { logsAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { exportTableToExcel } from '../../utils/excel';
import AuthMethodBadge, { authMeta } from '../../components/AuthMethodBadge/AuthMethodBadge';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import './GuestActivity.css';

/**
 * Guest Activity — what every guest does on the Wi-Fi, one row each time (backend
 * GuestActivity): the sign-in page came up, a code sent, signed in — how (the login
 * method, the plan, the Authorization Set) — or refused and why, internet before
 * sign-in, payments, online, offline, signed out. Each with the device (MAC, IP,
 * phone) and who it is (mobile, name, room). A mobile or a MAC clicked: that one
 * guest's whole story, summed up at the top.
 */

const EVENTS = {
    portal_opened:   { label: 'Opened the sign-in page', icon: 'fa-door-open' },
    otp_sent:        { label: 'Code sent', failed: 'Code not sent', icon: 'fa-comment-dots' },
    otp_verified:    { label: 'Number verified', failed: 'Wrong code', icon: 'fa-check-double' },
    signin:          { label: 'Signed in', failed: 'Sign-in refused', info: 'Waiting for approval', icon: 'fa-sign-in-alt' },
    prelogin:        { label: 'Internet before sign-in', failed: 'No internet before sign-in', info: 'Internet before sign-in closed', icon: 'fa-hourglass-half' },
    payment:         { label: 'Paid — plan on', info: 'Plan chosen', icon: 'fa-rupee-sign' },
    session_started: { label: 'Online', icon: 'fa-wifi' },
    session_ended:   { label: 'Offline', icon: 'fa-power-off' },
    signed_out:      { label: 'Signed out', icon: 'fa-sign-out-alt' },
};
const RESULTS = { ok: 'Done', failed: 'Failed / refused', info: 'Info' };
// The login methods are badges; the rest (a code's channel, a pre-login step, a gateway) plain words.
const PLAIN_METHODS = { whatsapp: 'WhatsApp', sms: 'SMS', connect: 'page opened', payment: 'before paying', signin: 'signed in' };
const LIVE_MS = 15000;

const EMPTY = { q: '', mac: '', mobile: '', event: '', outcome: '' };

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}
function fmtBytes(b) {
    const n = Number(b) || 0;
    if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
    if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
    if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${n} B`;
}
const eventLabel = (row) => {
    const e = EVENTS[row.event] || { label: row.event };
    return (row.outcome === 'failed' && e.failed) || (row.outcome === 'info' && e.info) || e.label;
};
const isLoginMethod = (method) => method && authMeta(method).icon !== 'fa-question-circle';

export default function GuestActivity() {
    const branchId = useBranchStore((s) => s.selectedBranch?._id);
    const tenantId = useBranchStore((s) => s.selectedTenant?._id);

    const [rows, setRows] = useState([]);
    const [guest, setGuest] = useState(null);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState(EMPTY);
    const [range, setRange] = useState({ from: '', to: '' });
    const [search, setSearch] = useState('');
    const [live, setLive] = useState(false);
    const [exporting, setExporting] = useState(false);
    const limit = 50;

    // Typed search waits for a pause before it asks.
    useEffect(() => {
        const t = setTimeout(() => { setFilters((f) => (f.q === search ? f : { ...f, q: search })); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const params = useCallback(() => {
        const p = { ...filters, ...rangeParams(range), branchId, tenantId };
        Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
        return p;
    }, [filters, range, branchId, tenantId]);

    const fetchRows = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            const r = await logsAPI.getGuestActivity({ ...params(), page, limit });
            setRows(r.data.data || []);
            setGuest(r.data.guest || null);
            setTotal(r.data.pagination?.total || 0);
        } catch (_) {
            if (!quiet) { setRows([]); setGuest(null); }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [params, page]);

    useEffect(() => { fetchRows(); }, [fetchRows]);
    useEffect(() => { setPage(1); }, [branchId, tenantId]);

    // Live: the newest page again every few seconds, quietly.
    const fetchRef = useRef(fetchRows);
    fetchRef.current = fetchRows;
    useEffect(() => {
        if (!live || page !== 1) return undefined;
        const t = setInterval(() => fetchRef.current(true), LIVE_MS);
        return () => clearInterval(t);
    }, [live, page]);

    const setFilter = (e) => { setFilters((f) => ({ ...f, [e.target.name]: e.target.value })); setPage(1); };
    const onRange = (next) => { setRange(next); setPage(1); };
    // One guest's whole story.
    const only = (name, value) => {
        if (!value) return;
        setGuest(null); // not the last guest's while theirs loads
        setSearch('');
        setFilters({ ...EMPTY, [name]: value });
        setPage(1);
    };
    const clearAll = () => { setGuest(null); setSearch(''); setFilters(EMPTY); setRange({ from: '', to: '' }); setPage(1); };

    async function handleExport() {
        setExporting(true);
        try {
            const r = await logsAPI.exportTable('activity', params());
            exportTableToExcel(`guest-activity-${new Date().toISOString().slice(0, 10)}`, r.data, { sheetName: 'Guest Activity' });
        } catch (_) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    }

    const pages = Math.ceil(total / limit);
    const oneGuest = filters.mobile || filters.mac;

    return (
        <div className="ga-wrap">
            <div className="ga-header">
                <div>
                    <h1 className="ga-title">Guest Activity</h1>
                    <p className="ga-sub">Everything guests do on the Wi-Fi — sign-in page, codes, sign-ins and how, payments, online and offline — with the device, its MAC and IP.</p>
                </div>
                <div className="ga-actions">
                    <label className={`ga-live ${live ? 'on' : ''}`} title={`Show new activity every ${LIVE_MS / 1000} seconds`}>
                        <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
                        <span className="ga-live-dot" aria-hidden="true"></span> Live
                    </label>
                    <button type="button" className="ga-btn" onClick={() => fetchRows()}>↻ Refresh</button>
                    <button type="button" className="ga-btn ga-btn-primary" onClick={handleExport} disabled={exporting || total === 0}>
                        <i className="fas fa-file-excel"></i> {exporting ? 'Exporting…' : 'Export Excel'}
                    </button>
                </div>
            </div>

            <div className="ga-filters">
                <input className="ga-search" placeholder="Search name, room, mobile, MAC or IP" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select name="event" value={filters.event} onChange={setFilter} aria-label="What happened">
                    <option value="">Everything</option>
                    {Object.entries(EVENTS).map(([key, e]) => <option key={key} value={key}>{e.label}</option>)}
                </select>
                <select name="outcome" value={filters.outcome} onChange={setFilter} aria-label="Result">
                    <option value="">Any result</option>
                    {Object.entries(RESULTS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
                <DateTimeRange value={range} onChange={onRange} />
                <button type="button" className="ga-btn" onClick={clearAll}>Clear</button>
            </div>

            {oneGuest && (
                <div className="ga-guest" role="region" aria-label="This guest">
                    <div className="ga-guest-head">
                        <strong>{filters.mobile ? `Mobile ${filters.mobile}` : `Device ${filters.mac}`}</strong>
                        <button type="button" className="ga-link" onClick={clearAll}>Show every guest</button>
                    </div>
                    {guest ? (
                        <dl className="ga-guest-facts">
                            {guest.names.length + guest.rooms.length > 0 && <div><dt>Name / room</dt><dd>{[...guest.names, ...guest.rooms.map((r) => `Room ${r}`)].join(', ')}</dd></div>}
                            {guest.mobiles.length > 0 && <div><dt>Mobile</dt><dd>{guest.mobiles.join(', ')}</dd></div>}
                            <div><dt>Devices (MAC)</dt><dd className="ga-mono">{guest.macs.join(', ') || '—'}</dd></div>
                            <div><dt>Phone</dt><dd>{guest.devices.join(', ') || '—'}</dd></div>
                            <div><dt>Last IP</dt><dd className="ga-mono">{guest.lastIp || '—'}</dd></div>
                            <div><dt>First seen</dt><dd>{fmt(guest.firstSeen)}</dd></div>
                            <div><dt>Last seen</dt><dd>{fmt(guest.lastSeen)}</dd></div>
                            <div>
                                <dt>Signed in with</dt>
                                <dd className="ga-badges">
                                    {guest.methods.length ? guest.methods.map((m) => <AuthMethodBadge key={m} method={m} small />) : '—'}
                                </dd>
                            </div>
                            {guest.authSets.length > 0 && <div><dt>Authorization Set</dt><dd>{guest.authSets.join(', ')}</dd></div>}
                            <div><dt>Sign-ins</dt><dd>{guest.signins} done{guest.signinsRefused ? ` · ${guest.signinsRefused} refused` : ''}</dd></div>
                            <div><dt>Sessions</dt><dd>{guest.sessions}{guest.bytes ? ` · ${fmtBytes(guest.bytes)} used` : ''}</dd></div>
                            {guest.paid > 0 && <div><dt>Paid</dt><dd>₹{guest.paid}</dd></div>}
                        </dl>
                    ) : (
                        <p className="ga-empty-inline">
                            {loading ? 'Loading…' : `Nothing recorded for this guest${range.from || range.to ? ' in this time' : ''}.`}
                        </p>
                    )}
                </div>
            )}

            <div className="ga-table-wrap">
                <table className="ga-table">
                    <thead>
                        <tr>
                            <th className="ga-sr">Sr. No.</th>
                            <th>Date &amp; time</th>
                            <th>Guest</th>
                            <th>Device · IP</th>
                            <th>What happened</th>
                            <th>Branch · router</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={6} className="ga-empty">Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={6} className="ga-empty">No activity found</td></tr>}
                        {!loading && rows.map((row, i) => {
                            const event = EVENTS[row.event] || {};
                            return (
                                <tr key={row._id} className={`ga-row ga-${row.outcome}`}>
                                    <td className="ga-sr">{(page - 1) * limit + i + 1}</td>
                                    <td className="ga-time">{fmt(row.createdAt)}</td>
                                    <td>
                                        {row.mobile
                                            ? <button type="button" className="ga-link" title="This guest's activity only" onClick={() => only('mobile', row.mobile)}>{row.mobile}</button>
                                            : <span className="ga-muted">—</span>}
                                        {(row.name || row.room) && <div className="ga-note">{[row.name, row.room && `Room ${row.room}`].filter(Boolean).join(' · ')}</div>}
                                    </td>
                                    <td className="ga-nowrap" title={row.userAgent || ''}>
                                        {row.mac
                                            ? <button type="button" className="ga-link ga-mono" title="This device's activity only" onClick={() => only('mac', row.mac)}>{row.mac}</button>
                                            : <span className="ga-muted">—</span>}
                                        <div className="ga-note">{[row.ip, row.device].filter(Boolean).join(' · ') || '—'}</div>
                                    </td>
                                    <td>
                                        <div className="ga-what">
                                            <span className={`ga-badge ga-badge-${row.outcome}`}>
                                                <i className={`fas ${event.icon || 'fa-circle'}`} aria-hidden="true"></i> {eventLabel(row)}
                                            </span>
                                            {isLoginMethod(row.method) && <AuthMethodBadge method={row.method} small />}
                                            {!isLoginMethod(row.method) && row.method && <span className="ga-method">{PLAIN_METHODS[row.method] || row.method}</span>}
                                        </div>
                                        {row.detail && <div className="ga-detail">{row.detail}</div>}
                                    </td>
                                    <td>
                                        {row.branchId?.name || '—'}
                                        {(row.nasId?.nasIdentifier || row.nasId?.name) && <div className="ga-note ga-mono">{row.nasId.nasIdentifier || row.nasId.name}</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="ga-pagination">
                <span>Total: {total}</span>
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                <span>Page {page} / {pages || 1}</span>
                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
            </div>
        </div>
    );
}
